import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Gauge, Redo } from 'lucide-react';
import type { ItemsAppInterface } from '../../vite-env';
import { Button } from 'primereact/button';
import { getComplexityStyle } from './complexityStyles';
import { ComplexityDot } from './ComplexityDot';

interface Props {
  itemsAppRef: ItemsAppInterface;
  currentItemRef: string | null;
  /** Problems the student has solved, as of the last "Check answer" or session restore. */
  passedReferences: string[];
  /** Whether this experiment variant marks solved problems at all. */
  showCompletionChecks: boolean;
  setIsOnLastQuestion: Dispatch<SetStateAction<boolean>>;
  handleReset: () => void;
  showRetryButton: boolean;
  skillId?: string;
}

export const extractComplexity = (tags: string[]): string | undefined => {
  return tags.find(tag => tag.includes('Complexity'))?.slice(-1);
};
export const PracticeProblemsAssessmentHeader: FC<Props> = ({
  itemsAppRef,
  currentItemRef,
  passedReferences,
  showCompletionChecks,
  setIsOnLastQuestion,
  handleReset,
  showRetryButton,
  skillId,
}) => {
  const tags = itemsAppRef.getTags();

  // Correctness comes from the parent's snapshot rather than from `getItemScores()`: Learnosity scores
  // a response as soon as it changes, so reading it here would check the dot the instant the student
  // picks the right option, before they press "Check answer". Gated at the source rather than at the
  // dot so the `aria-label` never claims a completion the variant does not show.
  const complexityByReference = Object.keys(tags).map(key => ({
    key,
    complexity: extractComplexity(tags[key]),
    isCorrect: showCompletionChecks && passedReferences.includes(key),
  }));

  // `currentItemRef` stays null until the first `item:load` fires, so fall back to asking Learnosity
  // directly to keep the badge and the wide segment correct on the very first render.
  const currentReference =
    currentItemRef ?? (complexityByReference.length > 0 ? itemsAppRef.getCurrentItem().reference : null);

  // The last segment of the progress track is the last problem, so both read off the same list.
  const lastReference = complexityByReference[complexityByReference.length - 1]?.key;
  const isOnLastQuestion = lastReference !== undefined && currentReference === lastReference;

  // Reported through an effect because the setter belongs to the parent: calling it during render
  // would update another component mid-render. Kept above the early return to satisfy hook rules.
  useEffect(() => {
    setIsOnLastQuestion(isOnLastQuestion);
  }, [isOnLastQuestion, setIsOnLastQuestion]);

  if (complexityByReference.length === 0) {
    return null;
  }

  const currentComplexity = complexityByReference.find(({ key }) => key === currentReference)?.complexity;
  const currentStyle = getComplexityStyle(currentComplexity);
  const total = complexityByReference.length;

  return (
    <div className="tw-w-full">
      <div className="tw-w-full tw-flex tw-block tw-justify-end">
        {showRetryButton && skillId && (
          <Button
            className="tw-font-atkinson tw-gap-x-2"
            icon={<Redo className="tw-color-blue-500" />}
            iconPos="left"
            label="Start over"
            link
            onClick={() => handleReset()}
          />
        )}
      </div>
      <div className="tw-w-full tw-flex tw-flex-row tw-items-center tw-justify-between tw-pb-4 tw-border-b-3 tw-border-t-0 tw-border-x-0 tw-border-solid tw-border-neutral-100">
        <ArrowLeft
          className="tw-cursor-pointer tw-text-blue-600 hover:tw-text-blue-700"
          onClick={() => itemsAppRef.items().previous()}
        />
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-3">
          {currentStyle.label && (
            <span
              className={`tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-xl tw-px-4 tw-py-1.5 tw-font-atkinson tw-text-sm tw-font-bold tw-tracking-wide ${currentStyle.badge}`}
            >
              <Gauge className="tw-h-5 tw-w-5" />
              {currentStyle.label}
            </span>
          )}

          <ol className="tw-flex tw-items-center tw-gap-x-2 tw-m-0 tw-p-0 tw-list-none">
            {complexityByReference.map(({ key, complexity, isCorrect }, index) => {
              const style = getComplexityStyle(complexity);
              const isCurrent = key === currentReference;

              return (
                <li
                  key={key}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`Problem ${index + 1} of ${total}${style.label ? `, ${style.label}` : ''}${
                    isCorrect ? ', answered correctly' : ''
                  }`}
                  className="tw-flex tw-items-center"
                >
                  <ComplexityDot complexity={complexity} isComplete={isCorrect} isCurrent={isCurrent} />
                </li>
              );
            })}
          </ol>
        </div>

        <ArrowRight
          className="tw-cursor-pointer tw-text-blue-600 hover:tw-text-blue-700"
          onClick={() => itemsAppRef.items().next()}
        />
      </div>
    </div>
  );
};
